/**
 * 参考 laserburn 思路：解析 $i 回显、推断机型、在 OTA 列表中匹配 resourceUrl（与 device/menu 中逻辑一致）。
 */

/** 与 laserburn `src/api/base.js` 中 `firmwareVersionList()` 的 path 一致 */
export const FIRMWARE_OTA_LIST_PATH = "/api/v1/device-ota/deviceOTA";

/** 与 laserburn `src/utils/request.js` 生产环境 baseURL 一致 */
const DEFAULT_OTA_API_BASE = "https://serve.longercloud.net:8234";

/** 唯一固件列表 GET 地址（改部署环境时只改此处） */
export const FIRMWARE_OTA_LIST_URL = `${DEFAULT_OTA_API_BASE.replace(/\/+$/, "")}${FIRMWARE_OTA_LIST_PATH}`;

/** 与 `scripts/ota-dev-proxy.cjs` 默认端口一致 */
export const OTA_DEV_PROXY_PORT = 8787;

/** 本地 dev 代理代拉 S3 固件（生产环境浏览器直连 S3，不走此路径） */
export const OTA_DOWNLOAD_PROXY_PATH = "/__ota_download";

/**
 * localhost / 127.0.0.1 → 8787 本地 dev 代理（OTA 列表 + 固件下载）。
 * 生产（如 https://ota.longercloud.net）→ 相对路径 /api/...（同源，不走 CORS），
 * 由站点 Nginx 反代到 serve.longercloud.net，并 strip 上游重复的 ACAO。
 * 生产固件 bin 由浏览器直连 S3（resourceUrl），需 S3 CORS 白名单包含 OTA 站点域名。
 */
function useOtaDevProxy(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/** 浏览器生产环境：相对路径，绝不直连 serve.longercloud.net:8234 */
function useOtaSameOriginProxy(): boolean {
  return typeof window !== "undefined" && !useOtaDevProxy();
}

/**
 * 固件 bin 下载 URL：
 * - 生产：浏览器直连 S3（resourceUrl）
 * - 本地 dev：8787 代理代拉 S3（localhost 通常不在 S3 CORS 白名单内）
 */
export function resolveFirmwareDownloadUrl(resourceUrl: string): string {
  const u = (resourceUrl || "").trim();
  if (!u) return u;
  if (useOtaDevProxy()) {
    const h = window.location.hostname;
    return `http://${h}:${OTA_DEV_PROXY_PORT}${OTA_DOWNLOAD_PROXY_PATH}?url=${encodeURIComponent(u)}`;
  }
  return u;
}

/** OTA 列表：本地 8787；线上相对路径 /api/...（需 Nginx 反代）。 */
export function getFirmwareListFetchUrl(): string {
  if (useOtaDevProxy()) {
    const h = window.location.hostname;
    return `http://${h}:${OTA_DEV_PROXY_PORT}${FIRMWARE_OTA_LIST_PATH}`;
  }
  if (useOtaSameOriginProxy()) {
    return FIRMWARE_OTA_LIST_PATH;
  }
  return FIRMWARE_OTA_LIST_URL;
}

/** 同源相对路径用默认 fetch；本地 dev 代理跨端口才需要 cors */
function otaFetch(input: string, init?: RequestInit): Promise<Response> {
  if (useOtaSameOriginProxy()) {
    return fetch(input, init);
  }
  return fetch(input, { ...init, mode: "cors" });
}

/** OTA `deviceType` value for MKS-based Longer Laser Old Ray5 (not the $i machineKey). */
export const OTA_DEVICE_TYPE_OLD_RAY5 = "oldRay5";

/** User-facing message when this model must not use online OTA (alert / simple flow). */
export const MSG_ONLINE_OTA_NOT_SUPPORTED =
  "Online firmware update is not supported for this device. Please contact customer service.";

export function isLongerLaserOldB1(machineKey: string): boolean {
  return machineKey === "Longer Laser OldB1";
}

export interface OtaFirmwareEntry {
  deviceType: string;
  resourceUrl?: string;
  latestVersion?: string;
  minimumSupportedVersion?: string;
  updateDescription?: string;
}

/** Backend may use camelCase or snake_case; normalize to OtaFirmwareEntry. */
function normalizeOtaRow(row: unknown): OtaFirmwareEntry | null {
  if (row == null || typeof row !== "object" || Array.isArray(row)) return null;
  const r = row as Record<string, unknown>;
  const s = (v: unknown) => (v == null ? "" : String(v).trim());
  const deviceType = s(r.deviceType ?? r.device_type ?? r.deviceTypeName ?? r.devicetype);
  if (!deviceType) return null;
  const resourceUrl = s(r.resourceUrl ?? r.resource_url ?? r.resourceURL ?? r.url);
  const latestVersion = s(r.latestVersion ?? r.latest_version ?? r.version);
  const minimumSupportedVersion = s(r.minimumSupportedVersion ?? r.minimum_supported_version);
  const updateDescription = s(r.updateDescription ?? r.update_description);
  return {
    deviceType,
    ...(resourceUrl ? { resourceUrl } : {}),
    ...(latestVersion ? { latestVersion } : {}),
    ...(minimumSupportedVersion ? { minimumSupportedVersion } : {}),
    ...(updateDescription ? { updateDescription } : {}),
  };
}

function normalizeOtaList(rows: unknown[]): OtaFirmwareEntry[] {
  const out: OtaFirmwareEntry[] = [];
  for (const row of rows) {
    const e = normalizeOtaRow(row);
    if (e) out.push(e);
  }
  return out;
}

export function parseIiBracketFields(rawText: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!rawText || typeof rawText !== "string") return out;
  const re = /\[([^\[\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rawText)) !== null) {
    const inner = m[1];
    const idx = inner.indexOf(":");
    if (idx === -1) continue;
    const key = inner.slice(0, idx).trim();
    const val = inner.slice(idx + 1).trim();
    if (key) out[key] = val;
  }
  return out;
}

/**
 * Resolve the canonical machine key from the raw $i response.
 *
 * Priority (high → low):
 *
 * 1. MB:LS_ESP32 anywhere in raw text → OldB1
 *    (dedicated hardware flag, always conclusive)
 *
 * 2. MKS in [Board:] or [PRODUCER:] → OldRay5
 *    Old Ray5 and new Ray5 share the same [Machine:Longer Laser Ray5] value,
 *    so we MUST distinguish them by board info before falling through to Machine.
 *    Old Ray5 uses an MKS DLC32 controller; new Ray5 does not.
 *
 * 3. [Machine:...] value (only reached when neither board-based check matched)
 *    For new Ray5 this returns "Longer Laser Ray5" which maps to the new-Ray5
 *    OTA entry. For other models (NanoDuo, etc.) it returns their own label.
 *
 * 4. Board contains "NanoDuo" (fallback when Machine field is empty)
 */
export function resolveCanonicalMachineKey(
  parsed: Record<string, string>,
  rawText: string,
): { machineKey: string; source: string } {
  const raw = rawText || "";
  const board = String(parsed.Board || "");
  const producer = String(parsed.PRODUCER || "");
  const machine = String(parsed.Machine || "").trim();

  // Priority 1 – hardware flag for OldB1
  if (raw.includes("MB:LS_ESP32")) {
    return { machineKey: "Longer Laser OldB1", source: "mb_ls_esp32" };
  }

  // Priority 2 – MKS board/producer → OldRay5
  // Must come before Machine field check because both old and new Ray5
  // report [Machine:Longer Laser Ray5]; only old Ray5 has an MKS controller.
  if (/MKS/i.test(board) || /MKS/i.test(producer)) {
    return { machineKey: "Longer Laser OldRay5", source: "mks_board_or_producer" };
  }

  // Priority 3 – Machine field (new Ray5, NanoDuo, and all other models)
  if (machine) {
    return { machineKey: machine, source: "machine_field" };
  }

  // Priority 4 – Board-inferred NanoDuo (Machine field absent)
  if (/NanoDuo/i.test(board)) {
    return { machineKey: "NanoDuo", source: "board_infer_nanoduo" };
  }

  return { machineKey: machine, source: "unknown" };
}

export function isMksRay5Firmware12x(parsed: Record<string, string>, machineKey: string): boolean {
  if (machineKey !== "Longer Laser OldRay5") return false;
  const sw = String(parsed.Software || "")
    .replace(/^V\s*/i, "")
    .trim();
  return /^1\.2\./.test(sw);
}

/** Normalize power labels such as "40W" / "40w" / "40 W" → "40W" (empty if not found). */
export function normalizePowerLabel(raw: string): string {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(\d+)\s*[wW]\b/);
  return m ? `${m[1]}W` : "";
}

/**
 * Ray5 OTA rows use updateDescription (e.g. "40W", "5W_limit", "10W_nolimit").
 * Prefer [Power:]; else parse from [Board:] e.g. "LGT LASER RAY5-40W V1.1".
 */
export function resolveRay5OtaPowerLabel(parsed: Record<string, string>): string {
  const fromPower = normalizePowerLabel(String(parsed.Power || ""));
  if (fromPower) return fromPower;
  return normalizePowerLabel(String(parsed.Board || ""));
}

/** Query limit-switch presence on new Ray5 (reply e.g. `$20=1` / `$20=0`). */
export const LIMIT_SWITCH_QUERY_CMD = "$20";

/** Parse `$20=1` (with limit) / `$20=0` (without). null if unrecognized. */
export function parseLimitSwitchFromD20(raw: string): boolean | null {
  const m = String(raw || "").match(/\$20\s*=\s*(\d+)/i);
  if (!m) return null;
  if (m[1] === "1") return true;
  if (m[1] === "0") return false;
  return null;
}

export function isLongerLaserRay5(machineKey: string): boolean {
  return machineKey === "Longer Laser Ray5";
}

export type Ray5MemorySize = 8 | 16;

/** Parse memory from `[OPT:PHSW,64,256,8]` → 8 or 16. */
export function parseRay5MemoryFromOpt(optValue: string): Ray5MemorySize | null {
  const parts = String(optValue || "")
    .split(",")
    .map((p) => p.trim());
  if (parts.length < 4 || parts[0].toUpperCase() !== "PHSW") return null;
  const mem = parseInt(parts[3], 10);
  if (mem === 8 || mem === 16) return mem;
  return null;
}

function parseFirmwareVersionTriple(
  software: string,
): { major: number; minor: number; patch: number } | null {
  const s = String(software || "")
    .replace(/^V\s*/i, "")
    .trim();
  const m = s.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return null;
  return { major: parseInt(m[1], 10), minor: parseInt(m[2], 10), patch: parseInt(m[3], 10) };
}

function firmwareVersionBelow(
  software: string,
  major: number,
  minor: number,
  patch: number,
): boolean {
  const v = parseFirmwareVersionTriple(software);
  if (!v) return false;
  if (v.major !== major) return v.major < major;
  if (v.minor !== minor) return v.minor < minor;
  return v.patch < patch;
}

/**
 * Ray5 flash size for OTA match (`N8` / `N16`).
 * - `[OPT:PHSW,64,256,8|16]` fourth field
 * - firmware < 1.9.14 without that field → N8
 */
export function resolveRay5MemorySize(parsed: Record<string, string>): Ray5MemorySize {
  const fromOpt = parseRay5MemoryFromOpt(parsed.OPT || "");
  if (fromOpt !== null) return fromOpt;
  if (firmwareVersionBelow(String(parsed.Software || ""), 1, 9, 14)) {
    return 8;
  }
  return 8;
}

/**
 * Parse Ray5 OTA updateDescription:
 * - `5W_limit_N8` / `10W_nolimit_N16` / `20W_N16`
 * - legacy without `_N8|16`: treated as N8, limit default true unless `_nolimit`
 */
export function parseRay5OtaUpdateDescription(desc: string): {
  power: string;
  hasLimit: boolean;
  memory: Ray5MemorySize;
} | null {
  const s = String(desc || "").trim();
  if (!s) return null;

  const limitMem = s.match(/^(\d+)\s*[wW]_limit_N(8|16)$/i);
  if (limitMem) {
    return {
      power: `${limitMem[1]}W`,
      hasLimit: true,
      memory: parseInt(limitMem[2], 10) as Ray5MemorySize,
    };
  }
  const noLimitMem = s.match(/^(\d+)\s*[wW]_nolimit_N(8|16)$/i);
  if (noLimitMem) {
    return {
      power: `${noLimitMem[1]}W`,
      hasLimit: false,
      memory: parseInt(noLimitMem[2], 10) as Ray5MemorySize,
    };
  }
  const powerMem = s.match(/^(\d+)\s*[wW]_N(8|16)$/i);
  if (powerMem) {
    return {
      power: `${powerMem[1]}W`,
      hasLimit: true,
      memory: parseInt(powerMem[2], 10) as Ray5MemorySize,
    };
  }

  const limitLegacy = s.match(/^(\d+)\s*[wW]_limit$/i);
  if (limitLegacy) {
    return { power: `${limitLegacy[1]}W`, hasLimit: true, memory: 8 };
  }
  const noLimitLegacy = s.match(/^(\d+)\s*[wW]_nolimit$/i);
  if (noLimitLegacy) {
    return { power: `${noLimitLegacy[1]}W`, hasLimit: false, memory: 8 };
  }
  const powerOnly = normalizePowerLabel(s);
  if (powerOnly) return { power: powerOnly, hasLimit: true, memory: 8 };
  return null;
}

export function ray5OtaUpdateDescriptionMatches(
  desc: string,
  devicePower: string,
  deviceHasLimit: boolean,
  deviceMemory: Ray5MemorySize,
): boolean {
  const ota = parseRay5OtaUpdateDescription(desc);
  if (!ota || !devicePower) return false;
  return (
    ota.power === devicePower && ota.hasLimit === deviceHasLimit && ota.memory === deviceMemory
  );
}

export interface MatchFirmwareOptions {
  /** New Ray5: true=带限位 ($20=1), false=不带 ($20=0). Default true when omitted. */
  ray5HasLimit?: boolean;
  /** 匹配过程诊断日志（输出到 xterm） */
  log?: (line: string) => void;
}

function ray5MatchFailureReason(
  desc: string,
  devicePower: string,
  deviceHasLimit: boolean,
  deviceMemory: Ray5MemorySize,
): string {
  const ota = parseRay5OtaUpdateDescription(desc);
  if (!ota) return "updateDescription format not recognized";
  const parts: string[] = [];
  if (ota.power !== devicePower) parts.push(`power: want ${devicePower}, got ${ota.power}`);
  if (ota.hasLimit !== deviceHasLimit) {
    parts.push(`limit: want ${deviceHasLimit ? "limit" : "nolimit"}, got ${ota.hasLimit ? "limit" : "nolimit"}`);
  }
  if (ota.memory !== deviceMemory) {
    parts.push(`memory: want N${deviceMemory}, got N${ota.memory}`);
  }
  return parts.length ? parts.join("; ") : "unknown";
}

function logFirmwareMatchDiagnostics(
  versionList: OtaFirmwareEntry[],
  parsed: Record<string, string>,
  machineKey: string,
  options: MatchFirmwareOptions | undefined,
  entry: OtaFirmwareEntry | undefined,
): void {
  const log = options?.log;
  if (!log) return;

  log("[OTA Match] ── device ──");
  log(`  machineKey: ${machineKey || "(empty)"}`);
  log(`  Board: ${parsed.Board || "-"}`);
  log(`  Machine: ${parsed.Machine || "-"}`);
  log(`  Software: ${parsed.Software || "-"}`);
  log(`  OPT: ${parsed.OPT || "-"}`);
  log(`  Power: ${parsed.Power || "-"}`);

  log(`[OTA Match] ── OTA list (${versionList?.length ?? 0} rows) ──`);
  if (!versionList?.length) {
    log("  (empty — check API / proxy)");
    return;
  }

  const sameType = versionList.filter((item) => item.deviceType === machineKey);
  const showRows = sameType.length ? sameType : versionList;

  if (machineKey === "Longer Laser Ray5") {
    const ray5Power = resolveRay5OtaPowerLabel(parsed);
    const hasLimit = options?.ray5HasLimit ?? true;
    const memory = resolveRay5MemorySize(parsed);
    log("[OTA Match] ── Ray5 criteria ──");
    log(`  power: ${ray5Power || "(not parsed)"}`);
    log(`  limit: ${hasLimit ? "limit ($20=1 or default)" : "nolimit ($20=0)"}`);
    log(`  memory: N${memory}${parsed.OPT ? "" : " (OPT absent, fw<1.9.14 → N8)"}`);
    log(`[OTA Match] ── candidates deviceType="${machineKey}" (${showRows.length}) ──`);
    for (const item of showRows) {
      const desc = item.updateDescription || "";
      const ota = parseRay5OtaUpdateDescription(desc);
      const ok =
        ray5Power !== "" &&
        ray5OtaUpdateDescriptionMatches(desc, ray5Power, hasLimit, memory);
      const parsedDesc = ota
        ? `${ota.power}/${ota.hasLimit ? "limit" : "nolimit"}/N${ota.memory}`
        : "?";
      if (ok) {
        log(`  ✓ desc="${desc}" → ${parsedDesc}  ver=${item.latestVersion || "-"}`);
      } else {
        const why =
          ray5Power === ""
            ? "device power not parsed from Board/Power"
            : ray5MatchFailureReason(desc, ray5Power, hasLimit, memory);
        log(`  ✗ desc="${desc}" → ${parsedDesc}  ver=${item.latestVersion || "-"}  (${why})`);
      }
    }
  } else if (machineKey.includes("RAY5mini")) {
    const power = normalizePowerLabel(String(parsed.Power || ""));
    log(`[OTA Match] ── RAY5mini criteria: power=${power || "(empty)"} ──`);
    for (const item of showRows) {
      const desc = normalizePowerLabel(item.updateDescription || "");
      const ok = item.deviceType === machineKey && desc === power && power !== "";
      log(
        `  ${ok ? "✓" : "✗"} deviceType="${item.deviceType}" desc="${item.updateDescription || "-"}" ver=${item.latestVersion || "-"}`,
      );
    }
  } else if (machineKey === "Longer Laser OldRay5") {
    log(`[OTA Match] ── OldRay5: deviceType="${OTA_DEVICE_TYPE_OLD_RAY5}" ──`);
    for (const item of versionList) {
      const dtNorm = (s: string) => s.trim().toLowerCase().replace(/[_\-\s]/g, "");
      const ok = dtNorm(item.deviceType) === "oldray5";
      log(
        `  ${ok ? "✓" : "·"} deviceType="${item.deviceType}" desc="${item.updateDescription || "-"}" ver=${item.latestVersion || "-"}`,
      );
    }
  } else {
    log(`[OTA Match] ── criteria: deviceType="${machineKey}" ──`);
    for (const item of showRows) {
      const ok = item.deviceType === machineKey;
      log(
        `  ${ok ? "✓" : "·"} deviceType="${item.deviceType}" desc="${item.updateDescription || "-"}" ver=${item.latestVersion || "-"}`,
      );
    }
  }

  if (sameType.length === 0 && machineKey) {
    const types = [...new Set(versionList.map((i) => i.deviceType))];
    log(`[OTA Match] no row with deviceType="${machineKey}"; available types: ${types.join(", ") || "-"}`);
  }

  if (entry?.resourceUrl) {
    log(
      `[OTA Match] OK → deviceType="${entry.deviceType}" desc="${entry.updateDescription || "-"}" ver=${entry.latestVersion || "-"}`,
    );
  } else {
    log("[OTA Match] FAILED — no matching firmware row");
  }
}

export function matchFirmwareResource(
  versionList: OtaFirmwareEntry[],
  parsed: Record<string, string>,
  machineKey: string,
  options?: MatchFirmwareOptions,
): {
  resourceUrl: string;
  latestVersion: string;
  deviceType: string;
  entry: OtaFirmwareEntry;
} | null {
  const list = versionList || [];
  const key = machineKey || "";

  if (!list.length || !key) {
    logFirmwareMatchDiagnostics(list, parsed, key || "(empty)", options, undefined);
    options?.log?.(
      !key
        ? "[OTA Match] FAILED early: machineKey empty (check $i / serial reply)"
        : "[OTA Match] FAILED early: OTA list empty",
    );
    return null;
  }

  const power = String(parsed.Power || "").trim();
  let entry: OtaFirmwareEntry | undefined;

  if (key.includes("RAY5mini")) {
    entry = versionList.find(
      (item) =>
        item.deviceType === key &&
        normalizePowerLabel(item.updateDescription || "") === normalizePowerLabel(power),
    );
  } else if (key === "Longer Laser Ray5") {
    const ray5Power = resolveRay5OtaPowerLabel(parsed);
    const hasLimit = options?.ray5HasLimit ?? true;
    const memory = resolveRay5MemorySize(parsed);
    if (ray5Power !== "") {
      entry = versionList.find(
        (item) =>
          item.deviceType === key &&
          ray5OtaUpdateDescriptionMatches(
            item.updateDescription || "",
            ray5Power,
            hasLimit,
            memory,
          ),
      );
    }
  } else if (key === "Longer Laser OldRay5") {
    // Old Ray5 OTA package is always named "oldRay5" (case-insensitive).
    // Do NOT fall back to matching [Machine:Longer Laser Ray5] — that is the
    // new Ray5's OTA name and would cause the wrong firmware to be selected.
    const dtNorm = (s: string) => s.trim().toLowerCase().replace(/[_\-\s]/g, "");
    const want = dtNorm(OTA_DEVICE_TYPE_OLD_RAY5); // "oldray5"
    entry = versionList.find((item) => dtNorm(item.deviceType) === want);
  } else {
    entry = versionList.find((item) => item.deviceType === key);
  }

  if (!entry && key === "NanoDuo") {
    entry = versionList.find(
      (item) =>
        item.deviceType === "NanoDuo" ||
        (typeof item.deviceType === "string" && /nanoduo/i.test(item.deviceType)),
    );
  }

  if (!entry || !entry.resourceUrl) {
    logFirmwareMatchDiagnostics(list, parsed, key, options, entry);
    return null;
  }

  logFirmwareMatchDiagnostics(list, parsed, key, options, entry);

  return {
    resourceUrl: entry.resourceUrl,
    latestVersion: entry.latestVersion || "",
    deviceType: entry.deviceType,
    entry,
  };
}

export function iiResponseLooksValid(raw: string): boolean {
  if (!raw || typeof raw !== "string") return false;
  return (
    raw.includes("[Board:") ||
    raw.includes("[Machine:") ||
    raw.includes("[Software:") ||
    raw.includes("MB:LS_ESP32")
  );
}

/** 页面加载时预取 OTA 列表；连接设备时复用同一次请求结果 */
let otaFirmwareListLoadPromise: Promise<OtaFirmwareEntry[]> | null = null;

async function loadOtaFirmwareListFromNetwork(log: (line: string) => void): Promise<OtaFirmwareEntry[]> {
  const u = getFirmwareListFetchUrl();
  if (useOtaSameOriginProxy() && u.includes("serve.longercloud.net")) {
    log("[OTA] 配置错误：生产环境不应直连 serve.longercloud.net，请重新 build 并部署");
  }
  try {
    const res = await otaFetch(u);
    const json = await res.json();
    if (json && json.code === 200 && Array.isArray(json.data)) {
      return normalizeOtaList(json.data);
    }
    if (Array.isArray(json)) {
      return normalizeOtaList(json);
    }
    log("[OTA] 接口返回格式无法识别（需 { code:200, data:[] } 或 JSON 数组）");
  } catch (e) {
    log(`[OTA] 请求失败: ${e instanceof Error ? e.message : String(e)}`);
  }
  return [];
}

function ensureOtaFirmwareListLoaded(log: (line: string) => void): Promise<OtaFirmwareEntry[]> {
  if (!otaFirmwareListLoadPromise) {
    otaFirmwareListLoadPromise = loadOtaFirmwareListFromNetwork(log);
  }
  return otaFirmwareListLoadPromise;
}

/** 进入页面时调用，便于后端联调（无需连接设备即可触发 OTA 列表请求） */
export function prefetchOtaFirmwareList(log: (line: string) => void = () => {}): Promise<OtaFirmwareEntry[]> {
  return ensureOtaFirmwareListLoaded(log);
}

export async function fetchOtaFirmwareList(log: (line: string) => void): Promise<OtaFirmwareEntry[]> {
  return ensureOtaFirmwareListLoaded(log);
}

/** ESP32 镜像首字节 magic（与 esptool image header 一致） */
export const ESP_IMAGE_MAGIC = 0xe9;

/** 正常 OTA 整包通常 ≥ 256KB；用于拦截 nginx SPA fallback 返回的 index.html（约 16KB） */
export const MIN_FIRMWARE_BYTES = 256 * 1024;

/** `total` 为 null 表示响应无 Content-Length，仅累计已读字节 */
export type FetchFirmwareProgress = (loaded: number, total: number | null) => void;

export function looksLikeHtmlOrTextPayload(buf: Uint8Array): boolean {
  if (!buf.byteLength) return false;
  const head = new TextDecoder("utf-8", { fatal: false }).decode(
    buf.subarray(0, Math.min(512, buf.byteLength)),
  );
  const t = head.trimStart().slice(0, 80).toLowerCase();
  return (
    t.startsWith("<!doctype") ||
    t.startsWith("<html") ||
    (t.startsWith("<?xml") && t.includes("<html"))
  );
}

/**
 * 烧录前校验：拒绝空包、HTML 错误页、非 ESP 镜像、明显过小的伪固件。
 */
export function validateFirmwareBinary(buf: Uint8Array): void {
  if (!buf.byteLength) {
    throw new Error("固件下载为空，无法烧录");
  }
  if (looksLikeHtmlOrTextPayload(buf)) {
    throw new Error(
      "下载结果是 HTML/文本页而非固件 bin。请确认 S3 resourceUrl 有效，且 OTA 站点域名已在 S3 CORS 白名单中。",
    );
  }
  if (buf[0] !== ESP_IMAGE_MAGIC) {
    throw new Error(
      `固件无效：ESP 镜像 magic 应为 0x${ESP_IMAGE_MAGIC.toString(16)}，实际 0x${buf[0].toString(16)}`,
    );
  }
  if (buf.byteLength < MIN_FIRMWARE_BYTES) {
    throw new Error(
      `固件过小（${buf.byteLength} 字节），疑似错误页或截断下载，正常 OTA 包通常为数 MB`,
    );
  }
}

function assertDownloadContentType(contentType: string): void {
  const ct = (contentType || "").trim();
  if (!ct) return;
  if (/text\/html/i.test(ct)) {
    throw new Error(
      `下载 Content-Type 为 text/html（${ct}）。S3 可能返回了错误页，请确认 resourceUrl 有效且 CORS 已配置。`,
    );
  }
}

export async function fetchUrlAsUint8Array(
  url: string,
  options?: { onProgress?: FetchFirmwareProgress; log?: (line: string) => void },
): Promise<Uint8Array> {
  const fetchUrl = resolveFirmwareDownloadUrl(url);
  options?.log?.(`[OTA] GET firmware ${fetchUrl}`);
  const res = await otaFetch(fetchUrl);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`);
  }
  assertDownloadContentType(res.headers.get("content-type") || "");
  const onProgress = options?.onProgress;

  const lenHeader = res.headers.get("content-length");
  const parsedLen =
    lenHeader != null && lenHeader !== "" ? parseInt(lenHeader, 10) : Number.NaN;
  const totalBytes =
    Number.isFinite(parsedLen) && parsedLen >= 0 ? parsedLen : null;

  const body = res.body;
  if (!body) {
    const buf = await res.arrayBuffer();
    onProgress?.(buf.byteLength, buf.byteLength);
    const out = new Uint8Array(buf);
    validateFirmwareBinary(out);
    options?.log?.(
      `[OTA] firmware OK: ${out.byteLength} bytes, magic 0x${out[0].toString(16)}`,
    );
    return out;
  }

  const reader = body.getReader();

  if (totalBytes !== null) {
    const out = new Uint8Array(totalBytes);
    let offset = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value || !value.byteLength) continue;
      const next = offset + value.byteLength;
      if (next > out.byteLength) {
        throw new Error("Download size exceeds Content-Length");
      }
      out.set(value, offset);
      offset = next;
      onProgress?.(offset, totalBytes);
    }
    if (offset !== out.byteLength) {
      throw new Error(`固件下载不完整：仅收到 ${offset} / ${out.byteLength} 字节`);
    }
    validateFirmwareBinary(out);
    options?.log?.(
      `[OTA] firmware OK: ${out.byteLength} bytes, magic 0x${out[0].toString(16)}`,
    );
    return out;
  }

  const chunks: Uint8Array[] = [];
  let loaded = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value && value.byteLength) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress?.(loaded, null);
    }
  }
  const out = new Uint8Array(loaded);
  let pos = 0;
  for (const c of chunks) {
    out.set(c, pos);
    pos += c.byteLength;
  }
  validateFirmwareBinary(out);
  options?.log?.(`[OTA] firmware OK: ${out.byteLength} bytes, magic 0x${out[0].toString(16)}`);
  return out;
}
